import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { Icon } from "@/components/Icon";

/**
 * An (i) button that shows a short note: on hover with a mouse, and on tap or click anywhere. It
 * closes when the pointer leaves (if hover opened it), on Escape, or on a tap outside.
 */
export function InfoTip({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState<"hover" | "click">();
  const id = useId();
  const box = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(undefined);
    };
    const onPointer = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(undefined);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  return (
    <span
      ref={box}
      className="info-tip"
      onPointerEnter={(event) => {
        if (event.pointerType === "mouse" && !open) setOpen("hover");
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === "mouse" && open === "hover") setOpen(undefined);
      }}
    >
      <button
        type="button"
        className="info-tip-button"
        aria-label={label}
        aria-expanded={open !== undefined}
        aria-controls={id}
        onClick={() => setOpen(open === "click" ? undefined : "click")}
      >
        <Icon name="info" size={18} />
      </button>
      {open && (
        <span id={id} role="note" className="info-tip-box">
          {children}
        </span>
      )}
    </span>
  );
}
